import { AlertCircle } from '@untitledui/icons';
import { useCallback, useState } from 'react';

import { Button } from '@/lib/base/buttons/button';
import { ConfirmationDialog } from '@/lib/base/overlays';
import { useSetMeetingVisibility } from '@/lib/crm/hooks/useSetMeetingVisibility';
import { ApiError } from '@/lib/errors';

// ─── Copy constants ───────────────────────────────────────────────────────────

const RESTRICT_BTN_LABEL = 'Restrict to attendees';
const SHARE_BTN_LABEL = 'Share with org';

const DIALOG_TITLE = 'Restrict this meeting?';
const DIALOG_CONFIRM_LABEL = 'Restrict';
const DIALOG_CANCEL_LABEL = 'Cancel';

const DIALOG_BODY_LINE_1 =
  "This meeting will be hidden from people who weren't attendees, everywhere including timelines.";
const DIALOG_BODY_LINE_2 =
  'Notes and transcript are included in this restriction.';

const ERROR_PERMISSION =
  "You don't have permission to change this meeting's visibility.";
const ERROR_NOT_FOUND = "This meeting wasn't found. It may have been deleted.";
const ERROR_RETRYABLE = "We couldn't change the visibility. Try again.";
const RETRY_BTN_LABEL = 'Retry';

// ─── Static class names ───────────────────────────────────────────────────────

const ERROR_REGION_CLASS =
  'flex items-start gap-2 rounded-lg bg-error-50 p-3 text-sm text-error-700';
const ERROR_ICON_CLASS = 'mt-0.5 size-4 shrink-0';
const ERROR_CONTENT_CLASS = 'flex flex-col gap-2';
const DIALOG_BODY_CLASS = 'flex flex-col gap-2';

// ─── Error helpers ────────────────────────────────────────────────────────────

function isNonRetryable(error: unknown): boolean {
  if (error instanceof ApiError) {
    return error.statusCode === 403 || error.statusCode === 404;
  }
  return false;
}

function deriveErrorMessage(error: unknown): string {
  if (error instanceof ApiError) {
    if (error.statusCode === 403) {
      return ERROR_PERMISSION;
    }
    if (error.statusCode === 404) {
      return ERROR_NOT_FOUND;
    }
  }
  return ERROR_RETRYABLE;
}

// ─── Error region ─────────────────────────────────────────────────────────────

interface ErrorRegionProps {
  error: unknown;
  onRetry: () => void;
  isRetrying: boolean;
}

function ErrorRegion({ error, onRetry, isRetrying }: ErrorRegionProps) {
  const nonRetryable = isNonRetryable(error);
  const message = deriveErrorMessage(error);

  return (
    <div role='alert' className={ERROR_REGION_CLASS}>
      <AlertCircle aria-hidden='true' className={ERROR_ICON_CLASS} />
      <div className={ERROR_CONTENT_CLASS}>
        <span>{message}</span>
        {!nonRetryable && (
          <Button
            color='secondary'
            size='xs'
            onClick={onRetry}
            isDisabled={isRetrying}
          >
            {RETRY_BTN_LABEL}
          </Button>
        )}
      </div>
    </div>
  );
}

// ─── Main component ───────────────────────────────────────────────────────────

export interface MeetingVisibilityActionsProps {
  orgId: number;
  meetingEntityId: string;
  privacyState: 'org' | 'private';
}

export function MeetingVisibilityActions({
  orgId,
  meetingEntityId,
  privacyState,
}: MeetingVisibilityActionsProps) {
  const mutation = useSetMeetingVisibility(orgId, meetingEntityId);
  const [isConfirmOpen, setIsConfirmOpen] = useState(false);
  const [lastTarget, setLastTarget] = useState<'private' | 'org'>('private');

  const runChange = useCallback(
    async (target: 'private' | 'org') => {
      setLastTarget(target);
      await mutation.mutateAsync(target);
    },
    [mutation]
  );

  const handleOpenConfirm = useCallback(() => {
    setIsConfirmOpen(true);
  }, []);

  const handleCloseConfirm = useCallback(() => {
    setIsConfirmOpen(false);
  }, []);

  const handleConfirmRestrict = useCallback(
    () => runChange('private'),
    [runChange]
  );

  const handleShare = useCallback(() => {
    runChange('org').catch(() => {});
  }, [runChange]);

  const handleRetry = useCallback(() => {
    runChange(lastTarget).catch(() => {});
  }, [runChange, lastTarget]);

  if (privacyState === 'org') {
    return (
      <>
        <Button
          color='secondary'
          size='sm'
          onClick={handleOpenConfirm}
          isDisabled={mutation.isPending}
        >
          {RESTRICT_BTN_LABEL}
        </Button>
        <ConfirmationDialog
          isOpen={isConfirmOpen}
          title={DIALOG_TITLE}
          variant='warning'
          confirmLabel={DIALOG_CONFIRM_LABEL}
          cancelLabel={DIALOG_CANCEL_LABEL}
          isLoading={mutation.isPending}
          onConfirm={handleConfirmRestrict}
          onCancel={handleCloseConfirm}
          onClose={handleCloseConfirm}
          instance='restrict-meeting'
        >
          <div className={DIALOG_BODY_CLASS}>
            <p>{DIALOG_BODY_LINE_1}</p>
            <p>{DIALOG_BODY_LINE_2}</p>
            {mutation.error && (
              <ErrorRegion
                error={mutation.error}
                onRetry={handleRetry}
                isRetrying={mutation.isPending}
              />
            )}
          </div>
        </ConfirmationDialog>
      </>
    );
  }

  return (
    <>
      <Button
        color='secondary'
        size='sm'
        onClick={handleShare}
        isDisabled={mutation.isPending}
      >
        {SHARE_BTN_LABEL}
      </Button>
      {mutation.error && (
        <ErrorRegion
          error={mutation.error}
          onRetry={handleRetry}
          isRetrying={mutation.isPending}
        />
      )}
    </>
  );
}
